<script setup lang="ts">
/**
 * Skeleton para cards de Tournaments - imita o layout real (horizontal)
 * Uso: <SmarticoApostatudoSkeletonTournamentsSkeleton :count="4" />
 */

interface Props {
  /** Quantidade de items skeleton */
  count?: number;
}

withDefaults(defineProps<Props>(), {
  count: 4,
});
</script>

<template>
  <div class="tournaments-skeleton">
    <div
      v-for="n in count"
      :key="`skeleton-${n}`"
      class="skeleton-card"
    >
      <!-- Imagem à esquerda -->
      <div class="skeleton-image" />

      <!-- Conteúdo à direita (tabela + footer) -->
      <div class="skeleton-content">
        <!-- Linhas da tabela (ranking) -->
        <div class="skeleton-table">
          <div
            v-for="row in 3"
            :key="`row-${row}`"
            class="skeleton-row"
          >
            <div class="skeleton-position" />
            <div class="skeleton-player" />
            <div class="skeleton-prize" />
          </div>
        </div>

        <!-- Footer: Timer + Botão -->
        <div class="skeleton-footer">
          <div class="skeleton-timer-icon" />
          <div class="skeleton-timer-text">
            <div class="skeleton-timer-label" />
            <div class="skeleton-timer-value" />
          </div>
          <div class="skeleton-button" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.tournaments-skeleton {
  @apply grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 items-stretch content-stretch;
}

.skeleton-card {
  @apply flex rounded-md overflow-hidden;
  background: rgba(0, 0, 0, 0.2);
}

/* Imagem à esquerda */
.skeleton-image {
  @apply flex-shrink-0 rounded-md;
  width: 140px;
  height: 140px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@media (min-width: 640px) {
  .skeleton-image {
    width: 160px;
    height: 160px;
  }
}

@media (min-width: 768px) {
  .skeleton-image {
    width: 210px;
    height: 210px;
  }
}

/* Conteúdo à direita */
.skeleton-content {
  @apply flex-1 flex flex-col p-2 md:p-3;
  background: linear-gradient(
    270deg,
    rgba(255, 255, 255, 0.05) 0%,
    rgba(255, 255, 255, 0) 100%
  );
}

/* Tabela */
.skeleton-table {
  @apply flex flex-col gap-1;
}

.skeleton-row {
  @apply flex items-center gap-2 rounded-md px-1;
  height: 22px;
  background: rgba(255, 255, 255, 0.05);
}

@media (min-width: 768px) {
  .skeleton-row {
    height: 32px;
    gap: 8px;
  }
}

.skeleton-position {
  @apply rounded flex-shrink-0;
  width: 24px;
  height: 16px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@media (min-width: 768px) {
  .skeleton-position {
    width: 26px;
    height: 20px;
  }
}

.skeleton-player {
  @apply rounded flex-1;
  height: 10px;
  max-width: 60px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@media (min-width: 768px) {
  .skeleton-player {
    height: 12px;
    max-width: 80px;
  }
}

.skeleton-prize {
  @apply rounded flex-1;
  height: 10px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@media (min-width: 768px) {
  .skeleton-prize {
    height: 12px;
  }
}

/* Footer */
.skeleton-footer {
  @apply flex items-center gap-2 mt-auto pt-2 md:pt-3;
}

.skeleton-timer-icon {
  @apply rounded-md flex-shrink-0;
  width: 20px;
  height: 20px;
  background: rgba(255, 255, 255, 0.1);
}

@media (min-width: 768px) {
  .skeleton-timer-icon {
    width: 32px;
    height: 32px;
  }
}

.skeleton-timer-text {
  @apply flex flex-col gap-1;
}

.skeleton-timer-label {
  @apply rounded;
  width: 40px;
  height: 8px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@media (min-width: 768px) {
  .skeleton-timer-label {
    width: 50px;
    height: 10px;
  }
}

.skeleton-timer-value {
  @apply rounded;
  width: 60px;
  height: 10px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@media (min-width: 768px) {
  .skeleton-timer-value {
    width: 70px;
    height: 12px;
  }
}

.skeleton-button {
  @apply rounded-md flex-1;
  height: 28px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@media (min-width: 768px) {
  .skeleton-button {
    height: 36px;
  }
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
